import React, { useState } from "react";
import {
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone,
  FaFax,
  FaClock,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaWhatsapp,
} from "react-icons/fa";

const ContactUs = () => {
  // form state
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({}); // <-- new: field-level errors
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success'|'error', text: string }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
    // clear field error as user types
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // improved validate: sets field errors and returns boolean
  const validate = () => {
    const newErrors = {};
    if (!form.name || !form.name.trim()) newErrors.name = "Please enter your name.";
    if (!form.email || !form.email.trim()) {
      newErrors.email = "Please enter your email.";
    } else {
      const re = /^\S+@\S+\.\S+$/;
      if (!re.test(form.email)) newErrors.email = "Please enter a valid email address.";
    }
    if (!form.message || !form.message.trim()) newErrors.message = "Please enter a message.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    const isValid = validate();
    if (!isValid) {
      setStatus({ type: "error", text: "Please fix the errors in the form." });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("https://email.sawtcode.com/api/email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "origin":"https://aktpwebsite.vercel.app/"
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          subject: form.subject || "Contact form",
          message: form.message,
        }),
      });

      if (!res.ok) {
        const json = await res.json().catch(() => null);
        const message = (json && json.message) || `Server returned ${res.status}`;
        throw new Error(message);
      }

      setStatus({ type: "success", text: "Message sent successfully. We'll get back to you soon." });
      setForm({ name: "", email: "", subject: "", message: "" });
      setErrors({});
    } catch (error) {
      setStatus({ type: "error", text: `Failed to send message. Please try again later` });
      console.log("error.message",error.message)
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-white via-yellow-50 to-yellow-100 relative overflow-hidden">
      {/* Animated Blobs */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl z-0" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-secondary/10 rounded-full blur-2xl z-0" />

      {/* Top Banner */}
      <div className="relative h-64 md:h-80 flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/images/industry.jpg')" }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-2 drop-shadow-lg">Contact Us</h1>
          <p className="text-sm md:text-base text-secondary">Home / Contact</p>
        </div>
      </div>

      {/* Intro */}
      <div className="max-w-3xl mx-auto text-center mt-10 mb-8 px-4">
        <h2 className="text-2xl md:text-3xl font-bold text-primary mb-3">We’re Here to Help</h2>
        <p className="text-gray-700 text-lg">
          Whether you have a question about our services, need a quote, or want to discuss your next project, our team is ready to answer all your questions. Reach out to us and we’ll respond as soon as possible!
        </p>
      </div>

      {/* Main Section */}
      <div className="py-16 px-2 md:px-4 relative overflow-visible">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-8 items-stretch">
          {/* Contact Info Card */}
          <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl w-full md:max-w-sm p-8 md:p-10 z-20 flex flex-col gap-8 border border-primary/10 relative">
            <div className="absolute -top-8 left-1/2 -translate-x-1/2">
              <div className="bg-primary text-white rounded-full w-16 h-16 flex items-center justify-center shadow-lg border-4 border-white text-3xl">
                <FaPhone />
              </div>
            </div>
            <div className="pt-10">
              <h2 className="text-2xl font-bold text-primary mb-6 text-center">Contact Information</h2>
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <span className="bg-red-100 text-red-500 rounded-full p-3 flex items-center justify-center shadow">
                    <FaMapMarkerAlt size={20} />
                  </span>
                  <span className="text-gray-700 font-medium text-sm">
                    3rd Cross, Prince Muhammad Street,<br />
                    Al-Khobar, Shamalia, Eastern Province,<br />
                    Saudi Arabia - 31952
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="bg-yellow-100 text-yellow-500 rounded-full p-3 flex items-center justify-center shadow">
                    <FaEnvelope size={20} />
                  </span>
                  <span className="text-gray-700 font-medium text-sm">Info@aktp-sa.com</span>
                </div>

                 <div className="flex items-center gap-4">
                  <span className="bg-blue-100 text-blue-500 rounded-full p-3 flex items-center justify-center shadow">
                    <FaFax size={20} />
                  </span>
                  <span className="text-gray-700 font-medium text-sm">sales@aktp-sa.com</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="bg-green-100 text-green-500 rounded-full p-3 flex items-center justify-center shadow">
                    <FaPhone size={20} />
                  </span>
                  <span className="text-gray-700 font-medium text-sm">+966 57 018 5488</span>
                </div>
               
              </div>
            </div>
            <div className="border-t border-primary/10 pt-6">
              <h3 className="text-lg font-semibold text-primary mb-2 flex items-center gap-2">
                <FaClock className="text-yellow-400" /> Business Hours
              </h3>
              <p className="text-gray-700 text-sm">
                Sunday - Thursday: 8:00 AM – 6:00 PM<br />
                Friday & Saturday: Closed
              </p>
            </div>
          </div>

          {/* Form Section */}
          <div className="bg-gradient-to-br from-white via-yellow-50 to-yellow-100 rounded-3xl shadow-2xl p-6 md:p-10 w-full relative z-10 border border-primary/10 overflow-hidden flex flex-col justify-center">
            {/* Decorative Icon */}
            <div className="absolute -top-8 right-8 opacity-10 text-primary text-8xl pointer-events-none select-none">
              <svg width="80" height="80" fill="none" viewBox="0 0 80 80"><circle cx="40" cy="40" r="40" fill="currentColor" /></svg>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div>
                <h2 className="text-3xl font-extrabold text-primary mb-2 flex items-center gap-3">
                  <span className="inline-block bg-primary/10 text-primary p-2 rounded-full">
                    <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><path d="M2 4v16h20V4H2zm2 2h16v12H4V6zm2 2v2h2V8H6zm0 4v2h2v-2H6zm0 4v2h2v-2H6zm4-8v2h8V8h-8zm0 4v2h8v-2h-8zm0 4v2h8v-2h-8z" fill="currentColor"/></svg>
                  </span>
                  Get In Touch
                </h2>
                <p className="text-gray-600">Feel free to drop us a line below!</p>
              </div>

              <a
                href="https://wa.me/966570185488"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-5 py-3 rounded-full font-semibold shadow transition"
                title="Chat on WhatsApp"
              >
                <FaWhatsapp size={20} />
                <span className="hidden md:inline">WhatsApp</span>
              </a>
            </div>

            {/* status message */}
            {status && (
              <div
                className={`mb-4 px-4 py-3 rounded-md text-sm ${
                  status.type === "success" ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"
                }`}
              >
                {status.text}
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="flex gap-4 flex-col md:flex-row">
                <div className="relative w-full md:w-1/2">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder=" "
                    className="peer w-full px-4 py-3 border border-gray-300 rounded-md bg-white/80 focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  <label
                    htmlFor="name"
                    className={`absolute left-4 transition-all duration-200 pointer-events-none bg-gradient-to-r from-white/80 to-yellow-50 px-1 ${
                      form.name ? "-top-4 text-xs text-primary" : "top-3 text-sm text-gray-500"
                    }`}
                  >
                    Your Name
                  </label>
                  {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
                </div>

                <div className="relative w-full md:w-1/2">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder=" "
                    className="peer w-full px-4 py-3 border border-gray-300 rounded-md bg-white/80 focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  <label
                    htmlFor="email"
                    className={`absolute left-4 transition-all duration-200 pointer-events-none bg-gradient-to-r from-white/80 to-yellow-50 px-1 ${
                      form.email ? "-top-4 text-xs text-primary" : "top-3 text-sm text-gray-500"
                    }`}
                  >
                    Your Email
                  </label>
                  {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
                </div>
              </div>

              <div className="relative">
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder=" "
                  className="peer w-full px-4 py-3 border border-gray-300 rounded-md bg-white/80 focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                <label
                  htmlFor="subject"
                  className={`absolute left-4 transition-all duration-200 pointer-events-none bg-gradient-to-r from-white/80 to-yellow-50 px-1 ${
                    form.subject ? "-top-4 text-xs text-primary" : "top-3 text-sm text-gray-500"
                  }`}
                >
                  Subject
                </label>
              </div>

              <div className="relative">
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  value={form.message}
                  onChange={handleChange}
                  placeholder=" "
                  className="peer w-full px-4 py-3 border border-gray-300 rounded-md bg-white/80 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                ></textarea>
                <label
                  htmlFor="message"
                  className={`absolute left-4 transition-all duration-200 pointer-events-none bg-gradient-to-r from-white/80 to-yellow-50 px-1 ${
                    form.message ? "-top-4 text-xs text-primary" : "top-3 text-sm text-gray-500"
                  }`}
                >
                  Type your message here...
                </label>
                {errors.message && <p className="text-red-600 text-sm mt-1">{errors.message}</p>}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className={`bg-primary text-white font-semibold py-3 px-8 rounded-full hover:bg-secondary transition-all duration-200 shadow-lg active:scale-95 ${loading ? "opacity-70 pointer-events-none" : ""}`}
                >
                  {loading ? "Sending..." : "SEND MESSAGE"}
                </button>
              </div>
            </form>

            <div className="my-8 border-t border-primary/10"></div>
            <div className="text-gray-400 text-xs text-center">
              We respect your privacy. Your information is safe with us.
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="flex items-center justify-center my-12">
        <span className="h-0.5 w-24 bg-primary/30 rounded-full"></span>
        <span className="mx-4 text-primary font-bold text-lg">Find Us On Map</span>
        <span className="h-0.5 w-24 bg-primary/30 rounded-full"></span>
      </div>

      {/* Map Section */}
      <div className="px-2 md:px-4 pb-24">
        <div className="max-w-6xl mx-auto rounded-2xl overflow-hidden shadow-2xl h-80 md:h-96">
          <iframe
            title="Google Map"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3620.122087161665!2d50.2141623!3d26.2834412!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e49e96893a355f7%3A0x3bdaf19bc9f1d4cf!2sAL%20KHOBAR%20TRADING%20PROJECT%20EST!5e0!3m2!1sen!2ssa!4v1692261234567!5m2!1sen!2ssa"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
